import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {ResourceType, type FileType} from "../../types";
import {bffApi} from "../../../chat/bffApi";
import {
    buildFileDownloadHref,
    buildFileTopicUrl,
    fileColorMap,
    fileExtensionMap,
    fileIconMap,
} from "../../contentUtils";
import DownloadIcon from "../../../../assets/download.svg?react";
import classes from "./RelatedFileLink.module.css";
import {metricUuid, newMetricEvent} from "../../../chat/metrics";

/** Download link for a related-content file item (right column + slider
 *  panel). Callers gate with isValidFileType — FileType enforces it. */
export const RelatedFileLink = ({
    name,
    resourceTypeId,
    resourceId,
    variant = "inline",
    className,
}: {
    name: string;
    resourceTypeId: FileType;
    resourceId: string;
    variant?: "row" | "inline";
    className?: string;
}) => {
    const {t} = useTranslation();
    const {data: userData} = bffApi.endpoints.getUser.useQueryState();
    const isDemo = !userData ? true : userData.demokonto;
    const [trackInteraction] = bffApi.endpoints.trackInteraction.useMutation();

    const Icon = fileIconMap[resourceTypeId];
    const iconColor = fileColorMap[resourceTypeId];

    // A PDF opens in the in-app viewer instead of downloading, as a plain
    // topic link (no download metric).
    if (String(resourceTypeId) === ResourceType.pdf) {
        return (
            <Link
                to={buildFileTopicUrl(name, resourceId)}
                className={`${classes.fileLink} ${classes[variant]} ${className ?? ""}`}
            >
                {name}
                {Icon && (
                    <Icon
                        className={classes.icon}
                        style={iconColor ? {color: iconColor} : undefined}
                        aria-hidden
                    />
                )}
            </Link>
        );
    }

    const title = isDemo
        ? t("file.downloadNotForDemo")
        : t("file.download", {file: name});

    return (
        <a
            href={isDemo ? undefined : buildFileDownloadHref(resourceId)}
            download={`${name}${fileExtensionMap[resourceTypeId] ?? ""}`}
            title={title}
            data-pw="file-download"
            aria-disabled={isDemo || undefined}
            className={`${classes.fileLink} ${classes[variant]} ${isDemo ? classes.disabled : ""} ${className ?? ""}`}
            onClick={() => {
                if (isDemo) return;
                void trackInteraction({
                    ...newMetricEvent(),
                    action: "file_download_requested",
                    subjectId: metricUuid(resourceId),
                    source: "content",
                    format: "unknown",
                });
            }}
        >
            {name}
            {Icon && (
                <>
                    <Icon
                        className={classes.icon}
                        style={iconColor ? {color: iconColor} : undefined}
                        aria-hidden
                    />
                    <DownloadIcon className={classes.downloadIcon} aria-hidden />
                </>
            )}
        </a>
    );
};
